import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { useSubsidiary } from '../../context/SubsidiaryContext';

const ProfileDiscoveryDialog = ({ open, onClose, connector, onProfilesAssigned }) => {
  const {
    subsidiaries,
    discoverProfiles,
    assignConnector,
    getProfileAssignments
  } = useSubsidiary();

  const [loading, setLoading] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Load profiles and existing assignments when dialog opens
  useEffect(() => {
    if (open && connector) {
      loadProfileData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, connector]);

  const loadProfileData = async () => {
    setDiscovering(true);
    setError(null);
    setProfiles([]);
    setAssignments({});

    try {
      // Discover profiles from the connector
      const discoverResult = await discoverProfiles(connector.id);

      if (discoverResult.success) {
        const discoveredProfiles = discoverResult.data.profiles || [];
        setProfiles(discoveredProfiles);

        // Load existing assignments
        const assignmentsResult = await getProfileAssignments(connector.id);
        if (assignmentsResult.success) {
          const existingAssignments = {};
          (assignmentsResult.data.assignments || []).forEach(assignment => {
            existingAssignments[assignment.profile_identifier] = assignment.subsidiary_id;
          });
          setAssignments(existingAssignments);
        }
      } else {
        setError(discoverResult.error || 'Failed to discover profiles');
      }
    } catch (err) {
      console.error('Error loading profile data:', err);
      setError('Failed to load profile information');
    } finally {
      setDiscovering(false);
    }
  };

  const handleAssignmentChange = (profileId, subsidiaryId) => {
    setAssignments(prev => ({
      ...prev,
      [profileId]: subsidiaryId
    }));
  };

  const handleSaveAssignments = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Save each profile assignment
      const promises = profiles.map(async (profile) => {
        const subsidiaryId = assignments[profile.profile_id];
        if (subsidiaryId) {
          return await assignConnector(subsidiaryId, {
            connectorId: connector.id,
            profileIdentifier: profile.profile_id,
            profileName: profile.profile_name,
            isPrimary: false,
            allocationPercentage: 100.0
          });
        }
        return { success: true };
      });

      const results = await Promise.all(promises);
      const allSuccessful = results.every(r => r.success);

      if (allSuccessful) {
        setSuccess(true);
        if (onProfilesAssigned) {
          onProfilesAssigned();
        }
        setTimeout(() => {
          handleClose();
        }, 1500);
      } else {
        setError('Some profile assignments failed');
      }
    } catch (err) {
      console.error('Error saving profile assignments:', err);
      setError('Failed to save profile assignments');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setProfiles([]);
    setAssignments({});
    setError(null);
    setSuccess(false);
    onClose();
  };

  // Get platform display name
  const getPlatformName = () => {
    if (!connector) return '';
    return connector.platform_display_name || connector.platform_name || '';
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AccountBalanceIcon sx={{ mr: 1 }} />
          <Box>
            <Typography variant="h6">
              Profile Discovery - {getPlatformName()}
            </Typography>
            {connector && (
              <Typography variant="body2" color="text.secondary">
                {connector.name}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {discovering && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Discovering profiles...
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Profile assignments saved successfully!
          </Alert>
        )}

        {!discovering && profiles.length > 0 && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Assign each profile to a subsidiary for proper financial tracking and reporting.
            </Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Profile Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Currency</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Assign To Subsidiary</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {profiles.map((profile) => (
                    <TableRow key={profile.profile_id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {profile.profile_name}
                        </Typography>
                        {profile.metadata?.business_name && (
                          <Typography variant="caption" color="text.secondary">
                            {profile.metadata.business_name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={profile.type}
                          size="small"
                          color={profile.type === 'business' ? 'primary' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {profile.currency || 'N/A'}
                      </TableCell>
                      <TableCell>
                        <FormControl fullWidth size="small">
                          <Select
                            value={assignments[profile.profile_id] || ''}
                            onChange={(e) => handleAssignmentChange(profile.profile_id, e.target.value)}
                            displayEmpty
                          >
                            <MenuItem value="">
                              <em>Select subsidiary...</em>
                            </MenuItem>
                            {subsidiaries.map((sub) => (
                              <MenuItem key={sub.id} value={sub.id}>
                                {sub.name}
                                {sub.is_default && ' (Default)'}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              Note: Each profile will be tracked separately for financial reporting.
            </Typography>
          </>
        )}

        {!discovering && profiles.length === 0 && !error && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <AccountBalanceIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              No profiles found for this connector
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSaveAssignments}
          variant="contained"
          disabled={loading || discovering || profiles.length === 0 || Object.keys(assignments).length === 0}
        >
          {loading ? <CircularProgress size={24} /> : 'Save Assignments'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProfileDiscoveryDialog;
